import { createFileRoute, useNavigate } from "@tanstack/react-router";
// Route definition
export const Route = createFileRoute("/admin/_layout/attendance/assignments")({
  component: RouteComponent,
});

function RouteComponent() {
  return <ShiftAssignmentDataTable />;
}

import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";

import { History } from "lucide-react";

import { SidebarInset } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";

// Hooks
import useFetchShiftAssignments, {
  type ShiftAssignmentOrdering,
} from "@/hooks/api/shift-assignment/useFetchShiftAssignment";
import useCreateShiftAssignment from "@/hooks/api/shift-assignment/useCreateShiftAssignment";
import useUpdateShiftAssignment from "@/hooks/api/shift-assignment/useUpdateShiftAssignment";
import useDeleteShiftAssignment from "@/hooks/api/shift-assignment/useDeleteShiftAssignment";
import useFetchShifts from "@/hooks/api/shift/useFetchShift";

// Components
import { ShiftAssignmentTable } from "@/components/crud/table/ShiftAssignmentTable";
import { ShiftAssignmentFormSheet } from "@/components/crud/forms/ShiftAssignmentFormSheet";
import { ShiftAssignmentBackfillDialog } from "@/components/crud/forms/ShiftAssignmentBackfillDialog";

import type { ShiftAssignment } from "@/types/shift-assignment";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import { DateFilter } from "@/components/crud/filters/DateFilter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { useDebounce } from "@/hooks/use-debounce";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SelectUser from "@/components/reusable-components/select-user";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

// Form data shape
interface ShiftAssignmentFormData {
  user_id: number;
  shift_id: number;
  date: string;
  start_time: string;
  end_time: string;
  is_recurring: boolean;
}

function ShiftAssignmentDataTable() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "attendance.shift.manage");
  // Filters and Search
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        date: SortDirection;
        start_time: SortDirection;
        created_at: SortDirection;
      },
      ShiftAssignmentOrdering
    >({
      date: null,
      start_time: null,
      created_at: null,
    });
  const [recurringFilter, setRecurringFilter] = useState<
    "true" | "false" | "clear" | undefined
  >();
  const [dateFilter, setDateFilter] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedGlobalFilter, resetDebounce] = useDebounce(searchQuery, 500);

  // React Hook Form for user filter
  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });

  const watchedUserId = watch("user_id");

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // CRUD hooks
  const shiftAssignmentsQuery = useFetchShiftAssignments({
    page_size: pageSize,
    page,
    search: debouncedGlobalFilter || undefined,
    ordering: getApiOrdering(),
    date: dateFilter,
    is_recurring:
      recurringFilter === "true"
        ? true
        : recurringFilter === "false"
          ? false
          : undefined,
    user: watchedUserId ? String(watchedUserId) : undefined,
  });
  const shiftAssignments = shiftAssignmentsQuery.data?.results ?? [];
  const count = shiftAssignmentsQuery.data?.count ?? 0;

  const createShiftAssignment = useCreateShiftAssignment();
  const updateShiftAssignment = useUpdateShiftAssignment();
  const deleteShiftAssignment = useDeleteShiftAssignment();

  // Shift picker for the assignment form (bounded list, no need for true
  // infinite scroll — search narrows it if an org ever has a large count)
  const [shiftSearchTerm, setShiftSearchTerm] = useState("");
  const shiftsQuery = useFetchShifts({
    search: shiftSearchTerm || undefined,
    page_size: 100,
  });
  const allShifts = shiftsQuery.data?.results ?? [];

  const [userSearchTerm, setUserSearchTerm] = useState("");
  const autoCompleteUsersQuery = useAutoCompleteUser({
    search: userSearchTerm || undefined,
  });
  const allUsers = useMemo(
    () => autoCompleteUsersQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteUsersQuery.data]
  );
  const autoCompleteLoading = autoCompleteUsersQuery.isFetching;

  // UI state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false); // Mode state
  const [editingAssignment, setEditingAssignment] =
    useState<ShiftAssignment | null>(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState<number | null>(
    null
  );
  const [assignmentToDelete, setAssignmentToDelete] =
    useState<ShiftAssignment | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBackfillOpen, setIsBackfillOpen] = useState(false);

  // Combined loading
  const isMutating =
    createShiftAssignment.isPending ||
    updateShiftAssignment.isPending ||
    deleteShiftAssignment.isPending;
  const isLoading = shiftAssignmentsQuery.isPending || isMutating;
  const isFetching = shiftAssignmentsQuery.isFetching || isMutating;

  // For resetting after create, update and delete
  const resetFilters = () => {
    setSearchQuery("");
    setOrdering({
      date: null,
      start_time: null,
      created_at: null,
    });
    setRecurringFilter(undefined);
    setDateFilter(undefined);
    resetDebounce();
    setPage(1);
    setValue("user_id", undefined);
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedGlobalFilter,
    ordering,
    recurringFilter,
    dateFilter,
    watchedUserId,
    setPage,
  ]);

  // Handlers
  const handleCreate = () => {
    setEditingAssignment(null);
    setEditingAssignmentId(null);
    setIsViewOnly(false); // Reset to edit mode
    setIsSheetOpen(true);
  };

  // Handler: View (Read Only)
  const handleView = (assignment: ShiftAssignment) => {
    setEditingAssignmentId(assignment.id);
    setEditingAssignment(assignment);
    setIsViewOnly(true); // Lock the form
    setIsSheetOpen(true);
  };

  const handleEdit = (assignment: ShiftAssignment) => {
    setEditingAssignmentId(assignment.id);
    setEditingAssignment(assignment);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (assignment: ShiftAssignment) => {
    setAssignmentToDelete(assignment);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) return;
    try {
      await deleteShiftAssignment.mutateAsync({ id: assignmentToDelete.id });
      toast.success("Assignment deleted successfully!");
      setIsDeleteDialogOpen(false);
      setAssignmentToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: ShiftAssignmentFormData) => {
    if (isViewOnly) return;
    if (editingAssignmentId) {
      await updateShiftAssignment.mutateAsync({
        id: editingAssignmentId,
        payload: data,
      });
      toast.success("Assignment updated successfully!");
    } else {
      await createShiftAssignment.mutateAsync(data);
      toast.success("Assignment created successfully!");
    }
    setIsSheetOpen(false);
  };

  //autoCompleteUsers code
  const handleSearchUsers = useCallback((query: string) => {
    setUserSearchTerm(query);
  }, []);

  const handleSearchShifts = useCallback((query: string) => {
    setShiftSearchTerm(query);
  }, []);

  //Pagination

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Shift Assignments"
          description="Assign employees to their respective work shifts."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Assignment"
          isLoading={isFetching}
        >
          {canManage && (
            <Button
              variant="outline"
              onClick={() => setIsBackfillOpen(true)}
              className="flex items-center gap-2"
            >
              <History className="h-4 w-4" />
              Backfill
            </Button>
          )}
        </PageHeader>
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={allUsers}
                onSearchUsers={handleSearchUsers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setValue={setValue}
                isLoading={autoCompleteLoading}
                fetchNextPage={autoCompleteUsersQuery.fetchNextPage}
                hasMore={autoCompleteUsersQuery.hasNextPage}
              />
            )}
          />

          <SelectFilter
            placeholder="Type"
            options={[
              { label: "One-time", value: "false" },
              { label: "Recurring", value: "true" },
              { label: "All", value: "clear" },
            ]}
            value={recurringFilter}
            onChange={setRecurringFilter}
          />

          <DateFilter
            placeholder="Select date"
            value={dateFilter}
            onChange={setDateFilter}
          />
        </FiltersPanel>

        <ShiftAssignmentTable
          assignments={shiftAssignments}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
          onUserClick={(assignment) =>
            navigate({
              to: "/admin/users/$id",
              params: { id: String(assignment.user.id) },
              search: { tab: "shift-assignments" },
            })
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <ShiftAssignmentFormSheet
          autoCompleteLoading={autoCompleteLoading}
          autoCompleteShiftsLoading={shiftsQuery.isFetching}
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          users={allUsers}
          shifts={allShifts}
          editingAssignment={editingAssignment}
          isLoading={isMutating}
          onSearchUsers={handleSearchUsers}
          onSearchShifts={handleSearchShifts}
          fetchNextUsersPage={autoCompleteUsersQuery.fetchNextPage}
          hasMoreUsers={autoCompleteUsersQuery.hasNextPage}
        />

        <ShiftAssignmentBackfillDialog
          isOpen={isBackfillOpen}
          onClose={() => setIsBackfillOpen(false)}
          users={allUsers}
          shifts={allShifts}
          autoCompleteLoading={autoCompleteLoading}
          autoCompleteShiftsLoading={shiftsQuery.isFetching}
          onSearchUsers={handleSearchUsers}
          onSearchShifts={handleSearchShifts}
          fetchNextUsersPage={autoCompleteUsersQuery.fetchNextPage}
          hasMoreUsers={autoCompleteUsersQuery.hasNextPage}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Assignment"
          description={`Are you sure you want to delete this assignment for "${assignmentToDelete?.user.first_name} ${assignmentToDelete?.user.last_name}"? This action cannot be undone.`}
          itemName={assignmentToDelete?.user.first_name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
